// src/pages/FileTransfers.js — Full file transfers list
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, formatDate, formatBytes, timeUntil, isManuallyDeleted, deletedLabel } from '../utils/api';
import TransferProgress from '../components/TransferProgress';
import PasswordResetModal from '../components/PasswordResetModal';

function StatusBadge({ status }) {
  const map = {
    active: ['status-active', '● Active'],
    'expired-downloaded': ['status-expired-downloaded', '✓ Downloaded'],
    'expired-timeout': ['status-expired-timeout', '⏱ Timed out'],
    deleted: ['status-deleted', '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function AddFilesModal({ transfer, onDone, onClose }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      const data = await apiFetch(`transfers/${transfer.id}/files`, {
        method: 'POST',
        body: JSON.stringify({ fileNames: files.map(f => f.name), fileSizes: files.map(f => f.size) }),
      });
      for (const file of files) {
        const sasUrl = data.uploadUrls[file.name];
        if (!sasUrl) continue;
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sasUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) setProgress(p => ({ ...p, [file.name]: Math.round(e.loaded / e.total * 100) }));
          });
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
      }
      onDone();
    } catch (e) { setError(e.message); setUploading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>➕ Add Files</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => setFiles(f => [...f, ...Array.from(e.target.files || [])])} />
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }} onClick={() => fileInputRef.current.click()}>📂 Choose files</button>
          {files.map(f => (
            <div key={f.name} className="file-item" style={{ marginBottom: '5px' }}>
              <span>📄</span><span className="file-item-name">{f.name}</span><span className="file-item-size">{formatBytes(f.size)}</span>
              {uploading && progress[f.name] !== undefined && <div className="progress-bar-wrap" style={{ width: '60px' }}><div className="progress-bar" style={{ width: `${progress[f.name]}%` }} /></div>}
              {!uploading && <button className="file-remove" onClick={() => setFiles(fs => fs.filter(x => x.name !== f.name))}>×</button>}
            </div>
          ))}
          {error && <div className="alert alert-error" style={{ marginTop: '10px' }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !files.length}>{uploading ? 'Uploading…' : `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`}</button>
        </div>
      </div>
    </div>
  );
}


function TransferCard({ transfer, onRefresh, showToast }) {
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState('');
  const [newPassword, setNewPassword] = useState(null);
  const isActive = transfer.status === 'active';
  const isExpired = !isActive && transfer.status !== 'deleted';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg || '✅ Done'); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  const expire = () => action('expire', () => apiFetch(`transfers/${transfer.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Transfer expired');
  const extend = () => action('extend', () => apiFetch(`transfers/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days');
  const reactivate = () => action('reactivate', () => apiFetch(`transfers/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-activated for 3 days');
  const resend = () => {
    if (!window.confirm(`Resend the notification email to ${transfer.recipientEmail}?`)) return;
    action('resend', () => apiFetch(`transfers/${transfer.id}/resend`, { method: 'POST', body: '{}' }), `✅ Email resent to ${transfer.recipientEmail}`);
  };
  const resetPassword = async () => {
    if (!window.confirm(`Generate a new password for ${transfer.recipientEmail}? The current password will stop working immediately.`)) return;
    setBusy('reset');
    try {
      const r = await apiFetch(`transfers/${transfer.id}/reset-password`, { method: 'POST', body: '{}' });
      setNewPassword(r.password);   // no list refresh here — it could unmount this card and lose the password
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };
  const deleteTransfer = () => {
    if (!window.confirm('Permanently delete this transfer?')) return;
    action('delete', () => apiFetch(`transfers/${transfer.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted');
  };
  const removeFile = fileName => {
    if (!window.confirm(`Remove "${fileName}"?`)) return;
    action('file', () => apiFetch(`transfers/${transfer.id}/files`, { method: 'DELETE', body: JSON.stringify({ fileName }) }), '✅ File removed');
  };

  return (
    <div className={`transfer-card${!isActive ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">{transfer.title}</div>
          <StatusBadge status={transfer.status} />
          {transfer.viewOnly && <span className="status-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>👁 View only</span>}
          {transfer.hasPassword && <span className="status-badge" style={{ background: '#fff8e1', color: '#b45309' }}>🔑 Password</span>}
        </div>
        <div className="transfer-meta">
          <span className="tp-recipient">✉ {transfer.recipientEmail}</span>
          <span>📅 Created {formatDate(transfer.createdAt)}</span>
          {isActive && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(transfer.expiresAt)}</span>}
          {!isActive && transfer.status !== 'deleted' && transfer.expiredAt && <span>Expired {formatDate(transfer.expiredAt)}</span>}
          {transfer.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(transfer)}</span>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
        <div style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--g400)', marginBottom: '8px' }}>Files ({transfer.fileNames.length})</div>
          {transfer.fileNames.map((name, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <span style={{ fontSize: '12px' }}>📄</span>
              <span style={{ fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--g700)' }}>{name}</span>
              <span style={{ fontSize: '11px', color: 'var(--g400)', whiteSpace: 'nowrap' }}>{formatBytes(transfer.fileSizes?.[i])}</span>
              {isActive && transfer.fileNames.length > 1 && <button disabled={!!busy} onClick={() => removeFile(name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: '14px', fontWeight: 700, padding: '0 2px', lineHeight: 1 }}>×</button>}
            </div>
          ))}
          {isActive && <button className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} onClick={() => setModal('addFile')} disabled={!!busy}>+ Add files</button>}
        </div>
        <div style={{ padding: '12px 18px', borderLeft: '1px solid var(--g100)' }}>
          <div className="tp-section-title">Recipient progress</div>
          <TransferProgress transfer={transfer} kind="file" />
        </div>
      </div>

      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--g100)', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isActive && (
          <>
            <button className={`btn btn-sm ${transfer.emailStatus === 'failed' ? 'btn-primary' : 'btn-secondary'}`} onClick={resend} disabled={!!busy}>{busy === 'resend' ? 'Sending…' : '📧 Resend email'}</button>
            {transfer.hasPassword && <button className="btn btn-secondary btn-sm" onClick={resetPassword} disabled={!!busy}>{busy === 'reset' ? '…' : '🔑 Reset password'}</button>}
            <button className="btn btn-secondary btn-sm" onClick={extend} disabled={!!busy}>{busy === 'extend' ? '…' : '⏱ Extend 3 days'}</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} onClick={expire} disabled={!!busy}>{busy === 'expire' ? '…' : '✕ Expire now'}</button>
          </>
        )}
        {isExpired && (
          <>
            {transfer.status !== 'expired-downloaded' && (
              <button className="btn btn-secondary btn-sm" onClick={reactivate} disabled={!!busy}>{busy === 'reactivate' ? '…' : '🔄 Re-activate (3 days)'}</button>
            )}
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} onClick={deleteTransfer} disabled={!!busy}>{busy === 'delete' ? '…' : '🗑 Delete transfer'}</button>
          </>
        )}
      </div>

      {newPassword && <PasswordResetModal password={newPassword} recipientEmail={transfer.recipientEmail} onClose={() => setNewPassword(null)} />}
      {modal === 'addFile' && <AddFilesModal transfer={transfer} onClose={() => setModal(null)} onDone={() => { setModal(null); onRefresh(); showToast('✅ Files added'); }} />}
    </div>
  );
}

export default function FileTransfers() {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [toast, setToast] = useState('');

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try { const data = await apiFetch('transfers/list'); setTransfers(data.transfers || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  const active = transfers.filter(t => t.status === 'active');
  const completed = transfers.filter(t => t.status === 'expired-downloaded');            // downloaded / viewed
  const deleted = transfers.filter(isManuallyDeleted);                                                       // deleted on purpose
  const expired = transfers.filter(t => t.status !== 'active' && t.status !== 'expired-downloaded' && !isManuallyDeleted(t));   // timed out / auto-purged
  const shown = tab === 'active' ? active : tab === 'completed' ? completed : tab === 'deleted' ? deleted : expired;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="page-eyebrow">Transfers</div>
          <h1 className="page-title">File Transfers</h1>
          <p className="page-subtitle">Files you have shared securely with external recipients</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/upload')}>⬆️ New File Transfer</button>
      </div>

      <div className="stat-grid">
        {[
          { label: 'Total', value: transfers.length, cls: 'red', icon: '📁' },
          { label: 'Active', value: active.length, cls: 'green', icon: '✅' },
          { label: 'Completed', value: completed.length, cls: 'blue', icon: '⬇️' },
          { label: 'Expired (not downloaded)', value: expired.filter(t => t.status === 'expired-timeout').length, cls: 'amber', icon: '⏱' },
        ].map(s => (
          <div key={s.label} className={`stat-card stat-card-${s.cls}`}>
            <div className={`stat-icon stat-icon-${s.cls}`}>{s.icon}</div>
            <div className="stat-info"><div className="stat-value">{s.value}</div><div className="stat-label">{s.label}</div></div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>Active <span className="tab-badge">{active.length}</span></button>
            <button className={`tab${tab === 'completed' ? ' active' : ''}`} onClick={() => setTab('completed')}>Completed <span className="tab-badge">{completed.length}</span></button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>Expired <span className="tab-badge">{expired.length}</span></button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>Deleted <span className="tab-badge">{deleted.length}</span></button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={loadTransfers}>↻ Refresh</button>
        </div>
        <div className="card-body">
          {loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : shown.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">{tab === 'active' ? '📭' : tab === 'completed' ? '✅' : tab === 'deleted' ? '🗑' : '🗂'}</div>
              <div className="empty-state-title">{tab === 'active' ? 'No active transfers' : tab === 'completed' ? 'No completed transfers' : tab === 'deleted' ? 'No deleted transfers' : 'No expired transfers'}</div>
              <div className="empty-state-desc" style={{ marginBottom: '16px' }}>{tab === 'active' ? 'Create a new transfer to share files securely.' : tab === 'completed' ? 'Transfers appear here once the recipient has downloaded them. They are kept for 30 days.' : tab === 'deleted' ? 'Transfers you delete appear here as a record. The files themselves are removed.' : 'Transfers that were not downloaded in time appear here. They are kept for 30 days.'}</div>
              {tab === 'active' && <button className="btn btn-primary" onClick={() => navigate('/upload')}>Create your first transfer</button>}
            </div>
          ) : (
            <div className="transfer-list">
              {shown.map(t => <TransferCard key={t.id} transfer={t} onRefresh={loadTransfers} showToast={showToast} />)}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
